import { skipToken, useQuery } from '@tanstack/react-query';
import { useRouteContext } from '@tanstack/react-router';

import type { ProjectModule } from '@cadstrom/sigma-engine-shared';

/**
 * Whether a module is marked DNP (do-not-populate).
 *
 * The selected package resolution is authoritative when one exists for the module; otherwise
 * falls back to the DNP flag parsed from the ECAD source, and finally to `false`.
 *
 * Reads the revision route's own module resolutions query, so every caller on a page shares one
 * request and one cache entry with the rest of that revision's consumers. Outside a project
 * revision route nothing is fetched and only the parsed flag is consulted.
 *
 * @param referenceDesignator - The module's project-level reference designator.
 * @param projectModule - The project module carrying the parsed DNP flag, when available.
 * @returns `true` when the module should be treated as DNP.
 */
export function useIsModuleDnp(referenceDesignator: string, projectModule?: ProjectModule): boolean {
	const getModuleResolutionsOptions = useRouteContext({
		strict: false,
		select: (context) => context.getModuleResolutionsOptions,
	});

	const { data: dnpByModuleRef } = useQuery({
		...getModuleResolutionsOptions,
		queryKey: getModuleResolutionsOptions?.queryKey ?? [['moduleResolution', 'unavailable']],
		queryFn: getModuleResolutionsOptions?.queryFn ?? skipToken,
		throwOnError: false,
		select: (data) => new Map(data.moduleResolutions.map((resolution) => [resolution.moduleRef, resolution.isDnp])),
	});

	return dnpByModuleRef?.get(referenceDesignator) ?? projectModule?.isDnp ?? false;
}
